import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram } from 'lucide-react';
import { navLinks } from '../data/mock';
import LanguageSelector from './LanguageSelector';
import { localizedPath, parsePathname, sectionId } from '../utils/routes';
import { usePageTransition } from './PageTransition';

const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'it';
  const { transitionTo } = usePageTransition();

  const translatedNavLinks = navLinks.map(link => ({
    ...link,
    label: t(`nav.${link.id}`)
  }));

  // Robust scroll handler for iOS Safari
  const handleScroll = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    setIsScrolled(scrollTop > 50);
  }, []);

  useEffect(() => {
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    let intervalId = null;
    
    const startPolling = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        handleScroll();
      }, 30);
    };
    
    const stopPolling = () => {
      setTimeout(() => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        handleScroll();
        setTimeout(handleScroll, 200);
        setTimeout(handleScroll, 500);
      }, 3500);
    };
    
    document.addEventListener('touchstart', startPolling, { passive: true });
    document.addEventListener('touchend', stopPolling, { passive: true });
    document.addEventListener('touchcancel', stopPolling, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchstart', startPolling);
      document.removeEventListener('touchend', stopPolling);
      document.removeEventListener('touchcancel', stopPolling);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [handleScroll]);

  const smoothScrollTo = useCallback((targetId) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    
    if (link.section) {
      const { page: currentPage } = parsePathname(location.pathname);
      const targetId = sectionId(currentLang, link.section);
      if (currentPage !== 'home') {
        navigate(localizedPath(currentLang, 'home'));
        setTimeout(() => smoothScrollTo(targetId), 500);
      } else {
        smoothScrollTo(targetId);
      }
    } else {
      const path = localizedPath(currentLang, link.page);
      const { page: currentPage } = parsePathname(location.pathname);
      if (currentPage === link.page) return;
      transitionTo(path);
    }
  };

  return (
    <>
      {/* Navbar */}
      <nav
        className="fixed top-0 left-0 right-0"
        style={{ 
          zIndex: 9999,
          backgroundColor: isScrolled ? '#0a0a0a' : 'transparent',
          transition: 'background-color 0.5s ease',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-24">
            {/* Logo */}
            <Link to={localizedPath(currentLang, 'home')}>
              <img 
                src="/images/logo_transparent.png" 
                alt="Double A Studio"
                className="h-12 w-auto object-contain"
                data-testid="logo-image"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-12">
              {translatedNavLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavClick(link)}
                  className="relative text-white/70 hover:text-white text-xs tracking-[0.2em] uppercase transition-colors duration-500 py-2 group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-full h-px bg-[#FFD700] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-400" />
                </button>
              ))}
              <LanguageSelector variant="navbar" />
              <a
                href="https://www.instagram.com/doubleastudioch"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/70 hover:text-[#FFD700] transition-colors duration-300"
                data-testid="navbar-instagram-link"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden relative w-10 h-10 flex items-center justify-center"
              style={{ zIndex: 10000 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <div className="relative w-6 h-4">
                <span
                  className={`absolute top-0 left-0 w-full h-px bg-white transition-all duration-300 ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''
                  }`}
                />
                <span
                  className={`absolute top-1/2 left-0 w-full h-px bg-[#FFD700] transition-opacity duration-300 ${
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                />
                <span
                  className={`absolute bottom-0 left-0 w-full h-px bg-white transition-all duration-300 ${
                    isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Full screen */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 md:hidden overflow-hidden touch-none"
            style={{ zIndex: 9998, backgroundColor: '#000000' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Gold accent line */}
            <motion.div
              className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-[#FFD700] via-[#FFD700]/50 to-transparent pointer-events-none"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.8 }}
            />

            <div className="h-full flex flex-col items-center justify-center pb-40 pt-16">
              {translatedNavLinks.map((link, index) => (
                <motion.button
                  key={link.id}
                  onClick={() => handleNavClick(link)}
                  className="font-display text-4xl text-white hover:text-[#FFD700] transition-colors duration-300 py-4"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.label}
                </motion.button>
              ))}
            </div>

            <motion.div
              className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <LanguageSelector variant="navbar" />
              <p className="text-white/50 text-xs tracking-[0.3em] uppercase">
                info@doubleastudio.ch
              </p>
              <a
                href="https://www.instagram.com/doubleastudioch"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white/50 hover:text-[#FFD700] transition-colors duration-300"
                data-testid="mobile-menu-instagram-link"
              >
                <Instagram className="w-4 h-4" />
                <span className="text-xs tracking-[0.2em] uppercase">Instagram</span>
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
